import { PropertyValue, Schema } from "src/api/flowTypes";
import {
  AiNodeV2ConversationMessageFilesListContentBlock,
  AiNodeV2ConversationMessageTextContentBlock,
  AiNodeV2InferenceConfig,
  AiNodeV2Meta,
  AiNodeV2ResponseFormatConfigTypeEnum,
  OutputAttribute,
} from "src/clients/flow-api";
import { PropertyUI, SchemaUI } from "src/schema/schemaMappingUtils";

export type ReasoningEffortLevel =
  | "disable"
  | "minimal"
  | "low"
  | "medium"
  | "high";

export const REASONING_EFFORT_LEVEL_LABELS = {
  disable: "Disable",
  minimal: "Minimal",
  low: "Low",
  medium: "Medium",
  high: "High",
};

export type ModelCapabilities = {
  temperature: {
    supported: boolean;
    min_value: number;
    max_value: number;
  };
  stop_sequences: {
    supported: boolean;
  };
  reasoning: {
    supported: boolean;
    available_effort_levels: ReasoningEffortLevel[];
  };
};

export type ModelStatus = "active" | "wip" | "deprecated";

export type BasicModelCard = {
  model_id: string;
  name: string;
  description: string;
  icon_path: string;
  providers?: string[];
  capabilities?: ModelCapabilities;
  status?: ModelStatus;
  deprecated_alternative?: string | null;
  routing_target_region?: string | null;
};

export type RenderableModelCard = BasicModelCard & {
  available: boolean;
};

export type AiNodeQuota = {
  cost?: {
    used: number | null;
    limit: number;
  };
  usage?: {
    [model_id: string]: {
      tokens_used: number | null;
      token_quota: number;
      requests_per_minute_limit: number;
      tokens_per_minute_limit: number;
    };
  };
};

export type AiNodePropertyBE = PropertyValue & {
  // `description` part of the JsonSchema standard
  description?: string;
  // `outputId` is a custom field that we use to tie together
  // the property with the output field
  outputId?: string;
};

export type AiNodeJsonSchemaBE = Schema<AiNodePropertyBE>;

export type AiNodeJsonSchemaPropertyUI = PropertyUI & {
  outputId: string;
  description?: string;
};

export type AiNodeJsonSchemaUI = SchemaUI & {
  $schema: string;
  properties: AiNodeJsonSchemaPropertyUI[];
  type: "object";
};

/* --- Autogenerated types fix (START) --- */
/**
 * The autogenerated type results in a `never[]` type for the `content` field.
 * This causes a lot of headaches, so explicitly redefining the type here.
 */
export type AiNodeV2MessageContentBlock =
  | AiNodeV2ConversationMessageTextContentBlock
  | AiNodeV2ConversationMessageFilesListContentBlock;

export type AiNodeV2MetaFixed = Omit<AiNodeV2Meta, "prompts"> & {
  prompts: Omit<AiNodeV2Meta["prompts"], "messages"> & {
    messages: Array<
      Omit<AiNodeV2Meta["prompts"]["messages"][number], "content"> & {
        content: Array<AiNodeV2MessageContentBlock>;
      }
    >;
  };
};

export const AI_NODE_V2_INSIGHTS = [
  "input_tokens_used",
  "output_tokens_used",
  "total_cost",
  "inference_latency",
] as const;

export type AiNodeV2Insight = (typeof AI_NODE_V2_INSIGHTS)[number];

/* --- Autogenerated types fix (END) --- */

// Redefining the json_schema field to reflect the structure we use on FE.
// BE is using the actual jsonschema format
export type AiNodeV2Form = Omit<AiNodeV2MetaFixed, "inference" | "insights"> & {
  insights: Record<AiNodeV2Insight, OutputAttribute>;
  inference: Omit<AiNodeV2InferenceConfig, "response_format"> & {
    response_format: {
      type: AiNodeV2ResponseFormatConfigTypeEnum;
      json_schema?: AiNodeJsonSchemaUI;
      original_json_schema?: AiNodeJsonSchemaBE;
    };
  };
};

// A subset of "Input/Output" schema types we want to support
export const AiNodeSelectableSchemaTypes = {
  string: "string",
  boolean: "boolean",
  number: "number",
  integer: "integer",
  object: "object",
  array: "array",
  enum: "enum",
  date: "date",
  datetime: "datetime",
} as const;
